import type { Order, OrderItem, OrderStatus, OrderType } from '../types';
import { getAmountPaid, getOrderTotalFromItems } from './orderUtils';

export type ItemLifecycleStatus =
  | 'DRAFT'
  | 'SENT'
  | 'ACCEPTED'
  | 'PREPARING'
  | 'READY'
  | 'SERVED'
  | 'COLLECTED'
  | 'HANDED_OVER'
  | 'VOID_REQUESTED'
  | 'VOIDED'
  | 'REMAKE'
  | 'REFUNDED';

export type OrderLifecycleStatus =
  | 'OPEN'
  | 'SENT'
  | 'IN_PROGRESS'
  | 'PARTIALLY_SERVED'
  | 'READY'
  | 'SERVED'
  | 'COLLECTED'
  | 'HANDED_OVER'
  | 'CLOSED'
  | 'REFUNDED'
  | 'VOIDED';

export interface ItemWithLifecycle extends OrderItem {
  /** Optional richer lifecycle; when absent we infer from existing flags. */
  itemLifecycleStatus?: ItemLifecycleStatus;
  /** When false, item is excluded from financial totals (e.g. remake, voided). */
  isBillable?: boolean;
}

export interface OrderWithLifecycle extends Order {
  items: ItemWithLifecycle[];
  orderLifecycleStatus?: OrderLifecycleStatus;
}

function inferItemLifecycleStatus(item: ItemWithLifecycle, orderType: OrderType): ItemLifecycleStatus {
  if (item.itemLifecycleStatus) return item.itemLifecycleStatus;
  if (item.removed) return 'VOIDED';
  if (item.isRemake) return 'REMAKE';
  if (item.quantityServed && item.quantityServed >= item.quantity) {
    if (orderType === 'takeaway' || orderType === 'online') return 'COLLECTED';
    if (orderType === 'uber-doordash' || orderType === 'delivery') return 'HANDED_OVER';
    return 'SERVED';
  }
  if (item.quantityCompleted && item.quantityCompleted >= item.quantity) return 'READY';
  if (item.status === 'completed') return 'READY';
  if (item.status === 'pending' && (item.quantityCompleted ?? 0) > 0) return 'PREPARING';
  return 'SENT';
}

export function deriveOrderLifecycleStatus(order: OrderWithLifecycle): OrderLifecycleStatus {
  const items = order.items ?? [];
  if (!items.length) return 'OPEN';

  const billableItems = items.filter((i) => i.isBillable !== false && !i.removed && i.price >= 0);
  if (!billableItems.length) return 'VOIDED';

  const lifecycleByItem = billableItems.map((i) => inferItemLifecycleStatus(i, order.orderType));
  const all = (state: ItemLifecycleStatus) => lifecycleByItem.every((s) => s === state);
  const any = (state: ItemLifecycleStatus) => lifecycleByItem.some((s) => s === state);

  const { orderType } = order;

  if (all('SENT')) return 'SENT';
  if (any('PREPARING') || any('READY')) {
    if (orderType === 'takeaway' || orderType === 'online' || orderType === 'uber-doordash' || orderType === 'delivery') {
      if (all('READY')) return 'READY';
    }
    return 'IN_PROGRESS';
  }

  const allServedLike =
    orderType === 'dine-in' || orderType === 'buffet'
      ? all('SERVED')
      : orderType === 'takeaway' || orderType === 'online'
      ? all('COLLECTED')
      : all('HANDED_OVER');

  if (allServedLike) {
    if (orderType === 'dine-in' || orderType === 'buffet') return 'SERVED';
    if (orderType === 'takeaway' || orderType === 'online') return 'COLLECTED';
    return 'HANDED_OVER';
  }

  const someServedLike =
    orderType === 'dine-in' || orderType === 'buffet'
      ? any('SERVED')
      : orderType === 'takeaway' || orderType === 'online'
      ? any('COLLECTED')
      : any('HANDED_OVER');

  if (someServedLike) return 'PARTIALLY_SERVED';

  return 'OPEN';
}

export function deriveDisplayStatus(order: OrderWithLifecycle): OrderLifecycleStatus {
  const lifecycle = order.orderLifecycleStatus ?? deriveOrderLifecycleStatus(order);

  const total = getOrderTotalFromItems(order);
  const paid = getAmountPaid(order);
  const isPaidOrExternal =
    order.orderType === 'uber-doordash' ||
    order.paymentStatus === 'paid' ||
    paid >= total - 0.01;

  if (lifecycle === 'VOIDED') return 'VOIDED';
  if (lifecycle === 'REFUNDED') return 'REFUNDED';
  if ((lifecycle === 'SERVED' || lifecycle === 'COLLECTED' || lifecycle === 'HANDED_OVER') && isPaidOrExternal) {
    return 'CLOSED';
  }
  return lifecycle;
}

export function mapOrderStatusToLegacy(status: OrderLifecycleStatus, type: OrderType): OrderStatus {
  switch (status) {
    case 'OPEN':
    case 'SENT':
      return 'new';
    case 'IN_PROGRESS':
      return 'preparing';
    case 'READY':
      return 'ready';
    case 'PARTIALLY_SERVED':
    case 'SERVED':
    case 'COLLECTED':
    case 'HANDED_OVER':
      return 'served';
    case 'REFUNDED':
      return 'refunded';
    case 'VOIDED':
      return type === 'buffet' ? 'buffet' : 'cancelled';
    case 'CLOSED':
      return 'served';
    default:
      return 'new';
  }
}

